/**
 * useBankruptcyReporter — fires exactly once per liquidation, posting the
 * post-mortem summary to /api/master/bankruptcy-alert. The backend persists
 * it on the apprentice license AND auto-creates a supervisor_note for the
 * master cohort (if any) so the Responsable Pédagogique sees it in their
 * dashboard.
 *
 * The hook is idempotent: it remembers the last reported liquidation day
 * in localStorage (per license key) so a refresh on the GameOverModal
 * doesn't re-fire.
 *
 * Storage note (security review 28/05/2026 v2) :
 *   The localStorage value here is JUST an idempotency token — the
 *   liquidation day number as a string ("42"), per license key. It carries
 *   NO secret, NO PII, NO session credential. It only prevents a duplicate
 *   POST on page refresh. Migrating this to an httpOnly cookie would
 *   force a backend round-trip on every refresh of GameOverModal for
 *   zero security benefit — explicit decision to keep localStorage.
 */
import { useEffect, useRef } from 'react';
import useGameStore from '../store/gameStore';
import useLicenseStore from '../store/licenseStore';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function storageKey(licenseKey) {
  return `hst-bankruptcy-reported-${licenseKey}`;
}

async function report(licenseKey, gameOverState) {
  if (!licenseKey || !BACKEND || !gameOverState) return;
  const lsKey = storageKey(licenseKey);
  // Avoid double-report (e.g. on refresh of the modal).
  try {
    if (typeof window !== 'undefined' && window.localStorage.getItem(lsKey) === String(gameOverState.liquidationDay)) {
      return;
    }
  } catch (e) {
    // localStorage unavailable (private mode, quota) — proceed anyway,
    // the backend deduplicates by (license_key, liquidation_day).
    // eslint-disable-next-line no-console
    console.warn('[bankruptcyReporter] localStorage unavailable:', e);
  }

  const { summary, liquidationDay, trigger } = gameOverState;
  const payload = {
    license_key: licenseKey,
    liquidation_day: liquidationDay,
    trigger,
    duration_days: summary?.durationDays || 0,
    total_revenue: summary?.totalRevenue || 0,
    total_expenses: summary?.totalExpenses || 0,
    final_debt: summary?.finalDebt || 0,
    causes: summary?.causes || [],
  };

  try {
    await fetch(`${BACKEND}/api/master/bankruptcy-alert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(lsKey, String(liquidationDay));
    }
  } catch (e) {
    // Offline — never crash the running app. We don't retry because the
    // game-over state is final and won't change.
    // eslint-disable-next-line no-console
    console.warn('[bankruptcyReporter] post-mortem upload failed:', e);
  }
}

export default function useBankruptcyReporter() {
  const licenseKey = useLicenseStore(s => s.licenseKey);
  const reportedDayRef = useRef(null);

  useEffect(() => {
    if (!licenseKey) return undefined;
    const unsubscribe = useGameStore.subscribe((state) => {
      const go = state.gameOverState;
      if (!go) return;
      if (reportedDayRef.current === go.liquidationDay) return; // already reported
      reportedDayRef.current = go.liquidationDay;
      report(licenseKey, go);
    });
    return unsubscribe;
  }, [licenseKey]);
}
